import { traverse, type ExprNode } from '@learnforge/demo-math/expr';
import { evaluate, sampleCurve } from './evaluate';

export interface VerticalAsymptote {
  x: number;
  sign: -1 | 1 | 'both';
}

export interface HorizontalAsymptote {
  y: number;
  direction: 'pos-inf' | 'neg-inf' | 'both';
}

const DIVERGENCE_THRESHOLD = 1e8;
const SAMPLES = 400;
const HORIZONTAL_PROBE_RADII = [
  1, 10, 100, 1_000, 10_000, 100_000, 1_000_000, 10_000_000, 100_000_000, 1_000_000_000,
  10_000_000_000, 100_000_000_000, 1_000_000_000_000,
] as const;
const HORIZONTAL_TAIL_SAMPLE_COUNT = 6;
const HORIZONTAL_MAX_STEP_RATIO = 0.5;
const HORIZONTAL_MIN_PLATEAU_STEPS = 3;
// Threshold for "diverging" neighbours of a sign-flipped gap. With 400 samples over [-10,10]
// the spacing (~0.05) means a simple pole like 1/(x-2) gives prev/next values around ±20,
// so the plan's 1e3 threshold misses it. 10 catches the simple cases without false positives
// on ordinary curves.
const NEIGHBOUR_DIVERGENCE = 10;

function refineAsymptoteX(node: ExprNode, lo: number, hi: number): number {
  let left = lo;
  let right = hi;
  const yLeft = evaluate(node, left);
  for (let iter = 0; iter < 20; iter++) {
    const mid = (left + right) / 2;
    const yMid = evaluate(node, mid);
    if (!Number.isFinite(yMid)) {
      right = mid;
    } else if (Math.sign(yMid) === Math.sign(yLeft)) {
      left = mid;
    } else {
      right = mid;
    }
  }
  return (left + right) / 2;
}

export function detectVerticalAsymptotes(
  node: ExprNode,
  a: number,
  b: number,
): VerticalAsymptote[] {
  const out: VerticalAsymptote[] = [];
  const buf = sampleCurve(node, a, b, SAMPLES);
  for (let i = 0; i < SAMPLES - 1; i++) {
    const cur = buf[i] as number;
    const next = buf[i + 1] as number;
    const xCur = a + ((b - a) * i) / (SAMPLES - 1);
    const xNext = a + ((b - a) * (i + 1)) / (SAMPLES - 1);
    if (
      Number.isFinite(cur) &&
      Number.isFinite(next) &&
      Math.sign(cur) !== Math.sign(next) &&
      Math.abs(cur) > NEIGHBOUR_DIVERGENCE &&
      Math.abs(next) > NEIGHBOUR_DIVERGENCE
    ) {
      out.push({ x: refineAsymptoteX(node, xCur, xNext), sign: 'both' });
      continue;
    }
    if (Number.isFinite(cur) && Math.abs(cur) > DIVERGENCE_THRESHOLD) {
      const sign: -1 | 1 = cur > 0 ? 1 : -1;
      const last = out[out.length - 1];
      if (!last || Math.abs(last.x - xCur) > 0.01) out.push({ x: xCur, sign });
    }
  }
  return out;
}

interface LimitEstimate {
  value: number;
  error: number;
}

function dependsOnX(node: ExprNode): boolean {
  let found = false;
  traverse(node, (candidate) => {
    if (candidate.kind === 'var' && candidate.name === 'x') found = true;
  });
  return found;
}

/**
 * Certify only a strong convergence trend at geometrically increasing radii.
 * Comparing successive step ratios makes the decision independent of vertical
 * scale: multiplying a divergent expression by a tiny coefficient cannot turn
 * constant/growing steps into contracting ones. Exact plateaus are accepted for
 * x-dependent expressions only after measurable contraction, so floating-point
 * under-resolution alone is not evidence of a limit.
 */
function estimateHorizontalLimit(
  node: ExprNode,
  direction: -1 | 1,
  hasXDependency: boolean,
): LimitEstimate | null {
  const probes = HORIZONTAL_PROBE_RADII.map((radius) => {
    const value = evaluate(node, direction * radius);
    return Number.isFinite(value) && Math.abs(value) < DIVERGENCE_THRESHOLD ? value : null;
  });

  let validStart = 0;
  for (let i = 0; i < probes.length; i++) {
    if (probes[i] === null) validStart = i + 1;
  }
  const values = probes.slice(validStart) as number[];
  if (values.length < HORIZONTAL_TAIL_SAMPLE_COUNT) return null;

  const deltas = values.slice(1).map((value, index) => Math.abs(value - values[index]!));
  let trailingPlateauSteps = 0;
  for (let i = deltas.length - 1; i >= 0 && deltas[i] === 0; i--) {
    trailingPlateauSteps++;
  }

  if (trailingPlateauSteps >= HORIZONTAL_MIN_PLATEAU_STEPS) {
    const lastMovingStep = deltas.length - trailingPlateauSteps - 1;
    if (lastMovingStep < 0) {
      if (hasXDependency) return null;
    } else {
      const previousStep = deltas[lastMovingStep - 1];
      if (
        previousStep === undefined ||
        previousStep === 0 ||
        deltas[lastMovingStep]! > previousStep * HORIZONTAL_MAX_STEP_RATIO
      ) {
        return null;
      }
    }
    return { value: values.at(-1)!, error: 0 };
  }

  const tail = deltas.slice(-(HORIZONTAL_TAIL_SAMPLE_COUNT - 1));
  if (tail.some((delta) => delta === 0)) return null;
  for (let i = 1; i < tail.length; i++) {
    if (tail[i]! > tail[i - 1]! * HORIZONTAL_MAX_STEP_RATIO) return null;
  }
  return { value: values.at(-1)!, error: tail.at(-1)! };
}

export function detectHorizontalAsymptotes(node: ExprNode): HorizontalAsymptote[] {
  const hasXDependency = dependsOnX(node);
  const yPos = estimateHorizontalLimit(node, 1, hasXDependency);
  const yNeg = estimateHorizontalLimit(node, -1, hasXDependency);
  const out: HorizontalAsymptote[] = [];
  if (
    yPos !== null &&
    yNeg !== null &&
    Math.abs(yPos.value - yNeg.value) <= yPos.error + yNeg.error
  ) {
    out.push({ y: (yPos.value + yNeg.value) / 2, direction: 'both' });
    return out;
  }
  if (yPos !== null) out.push({ y: yPos.value, direction: 'pos-inf' });
  if (yNeg !== null) out.push({ y: yNeg.value, direction: 'neg-inf' });
  return out;
}
